import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Download, TrendingUp, TrendingDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Reports = () => {
  const [financial, setFinancial] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [financialRes, projectsRes] = await Promise.all([
        axiosInstance.get('/reports/financial'),
        axiosInstance.get('/reports/projects')
      ]);
      setFinancial(financialRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const downloadFinancialReport = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Financial Report', 105, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 28, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('Summary', 20, 45);
    
    const summaryData = [
      ['Total Income', `${financial.total_income.toFixed(2)} SAR`],
      ['Total Expenses', `${financial.total_expenses.toFixed(2)} SAR`],
      ['Net Profit', `${financial.net_profit.toFixed(2)} SAR`],
      ['Profit Margin', `${financial.profit_margin.toFixed(2)}%`]
    ];
    
    autoTable(doc, {
      startY: 50,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] }
    });
    
    const monthlyData = Object.keys(financial.income_by_month).map(month => [
      month,
      `${(financial.income_by_month[month] || 0).toFixed(2)} SAR`,
      `${(financial.expense_by_month[month] || 0).toFixed(2)} SAR`,
      `${((financial.income_by_month[month] || 0) - (financial.expense_by_month[month] || 0)).toFixed(2)} SAR`
    ]);
    
    doc.setFontSize(14);
    const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 100;
    doc.text('Monthly Breakdown', 20, finalY);
    
    autoTable(doc, {
      startY: finalY + 5,
      head: [['Month', 'Income', 'Expenses', 'Profit']],
      body: monthlyData,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] }
    });
    
    // Download PDF directly
    doc.save('Financial_Report.pdf');
    toast.success('PDF downloaded');
  };

  const downloadProjectsReport = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Projects Report', 105, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 28, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('Summary', 20, 45);
    
    const summaryData = [
      ['Total Projects', projects.total_projects.toString()],
      ['Total Budget', `${projects.total_budget.toFixed(2)} SAR`],
      ['Total Spent', `${projects.total_spent.toFixed(2)} SAR`],
      ['Budget Utilization', `${projects.budget_utilization.toFixed(2)}%`]
    ];
    
    autoTable(doc, {
      startY: 50,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] }
    });
    
    const statusData = Object.keys(projects.by_status).map(status => [
      status.replace('_', ' ').toUpperCase(),
      projects.by_status[status].toString()
    ]);
    
    doc.setFontSize(14);
    const finalY2 = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : 100;
    doc.text('Projects by Status', 20, finalY2);
    
    autoTable(doc, {
      startY: finalY2 + 5,
      head: [['Status', 'Count']],
      body: statusData,
      theme: 'striped',
      headStyles: { fillColor: [102, 126, 234] }
    });
    
    // Download PDF directly
    doc.save('Projects_Report.pdf');
    toast.success('PDF downloaded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading reports...</p>
        </div>
      </div>
    );
  }

  const monthlyData = Object.keys(financial?.income_by_month || {}).map(month => ({
    month,
    income: financial?.income_by_month[month] || 0,
    expenses: financial?.expense_by_month[month] || 0,
    profit: (financial?.income_by_month[month] || 0) - (financial?.expense_by_month[month] || 0)
  })).sort((a, b) => a.month.localeCompare(b.month));

  const statusData = Object.keys(projects?.by_status || {}).map(status => ({
    name: status.replace('_', ' ').toUpperCase(),
    value: projects.by_status[status]
  }));

  const COLORS = ['#667eea', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6'];

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <h2 className="page-title">Reports</h2>
      </div>

      {/* Financial Summary */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900">Financial Overview</h3>
          <button
            data-testid="download-financial-report"
            onClick={downloadFinancialReport}
            className="btn-primary"
          >
            <Download className="w-4 h-4 inline mr-2" />
            Download PDF
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="stat-card">
            <div className="stat-icon green">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Total Income</h4>
            <p className="text-2xl font-bold text-gray-900">{(financial?.total_income || 0).toLocaleString()} SAR</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon red">
              <TrendingDown className="w-6 h-6" />
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Total Expenses</h4>
            <p className="text-2xl font-bold text-gray-900">{(financial?.total_expenses || 0).toLocaleString()} SAR</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon blue">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Net Profit</h4>
            <p className="text-2xl font-bold text-gray-900">{(financial?.net_profit || 0).toLocaleString()} SAR</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">
              <TrendingUp className="w-6 h-6" />
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Profit Margin</h4>
            <p className="text-2xl font-bold text-gray-900">{financial?.profit_margin?.toFixed(1) || 0}%</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="chart-container">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Monthly Income vs Expenses</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="income" fill="#10b981" name="Income" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="chart-container">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Profit Trend</h4>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="profit"
                  stroke="#667eea"
                  strokeWidth={3}
                  name="Net Profit"
                  dot={{ fill: '#667eea', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Projects Summary */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900">Projects Overview</h3>
          <button
            data-testid="download-projects-report"
            onClick={downloadProjectsReport}
            className="btn-primary"
          >
            <Download className="w-4 h-4 inline mr-2" />
            Download PDF
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="stat-card">
            <div className="stat-icon blue">
              <span className="text-2xl">📋</span>
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Total Projects</h4>
            <p className="text-2xl font-bold text-gray-900">{projects?.total_projects || 0}</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon green">
              <span className="text-2xl">💰</span>
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Total Budget</h4>
            <p className="text-2xl font-bold text-gray-900">{(projects?.total_budget || 0).toLocaleString()} SAR</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon red">
              <span className="text-2xl">📉</span>
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Total Spent</h4>
            <p className="text-2xl font-bold text-gray-900">{(projects?.total_spent || 0).toLocaleString()} SAR</p>
          </div>

          <div className="stat-card">
            <div className="stat-icon purple">
              <span className="text-2xl">🎯</span>
            </div>
            <h4 className="text-gray-600 text-sm font-medium mb-1">Budget Utilization</h4>
            <p className="text-2xl font-bold text-gray-900">{projects?.budget_utilization?.toFixed(1) || 0}%</p>
          </div>
        </div>

        <div className="chart-container">
          <h4 className="text-lg font-bold text-gray-900 mb-4">Projects by Status</h4>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Reports;